/**
 * Telemetry series registry — the single source of truth for every
 * metric the engineering telemetry view plots. The chart group, legend,
 * tooltip, and per-row label column all read from this list, so adding
 * a new metric is a one-place change.
 *
 * Each entry describes:
 *   id        — stable identifier; used in the URL `hide` param. Once
 *               shipped, do NOT rename — that would silently invalidate
 *               every deep link.
 *   label     — short human label shown in the row's label column and
 *               in the tooltip.
 *   section   — visual grouping (Host / Network / Sensors / Projector).
 *               Sections render with a section header above their first
 *               row and a per-section legend cluster below the chart.
 *   unit      — display unit. Empty string when the metric is unitless
 *               (e.g. modem signal strength as a 0-100 score).
 *   accessor  — pulls the numeric value out of a TelemetryPoint. Return
 *               null for sentinel / missing values so the polyline
 *               correctly skips the gap (the chart code segments
 *               consecutive non-null runs).
 *   format    — value→string for tooltip + y-axis hints. Receives a
 *               finite number; null handling is the caller's job.
 *   color     — fill / stroke colour for this series. Picked so that
 *               every metric within a section is visually distinct;
 *               across sections, repetition is fine because sections
 *               never appear in the same legend cluster.
 *   yMin/yMax — optional fixed y-axis bounds. Use 0-100 for percentages
 *               so a low-utilisation period doesn't auto-zoom into the
 *               noise floor.
 *
 * Y-scale rule used by TelemetryRow:
 *   - Series with BOTH yMin and yMax declared use those bounds verbatim.
 *   - Otherwise (auto-scaled), the row uses ROBUST scaling: 5th–95th
 *     percentile of the non-null data, extended to include the latest
 *     value. Points outside the range still render (clipped at the
 *     SVG edge) and a small triangle marker indicates "off-scale value
 *     here". This means a single corrupt 2420 h reading in lamp_hours
 *     doesn't flatten the polyline into invisibility — the bulk of the
 *     data stays readable.
 */
import type { TelemetryPoint } from '../../api/hooks';

export type TelemetrySection = 'Host' | 'Network' | 'Sensors' | 'Projector';

export interface TelemetrySeries {
  id: string;
  label: string;
  section: TelemetrySection;
  unit: string;
  accessor: (p: TelemetryPoint) => number | null;
  format: (v: number) => string;
  color: string;
  yMin?: number;
  yMax?: number;
}

// 255 is the spec sentinel for "remote not connected" — treat as missing
// so the chart doesn't plot a flat 255-line that swamps the real data.
function batteryAccessor(p: TelemetryPoint): number | null {
  const v = p.remote_battery_min;
  if (v === null || v === undefined) return null;
  if (v === 255) return null;
  return v;
}

// Projector NTC thermistors report -100 as the "no measurement" sentinel
// (model doesn't expose this sensor, or sensor is offline) — see DE-135
// and DeviceStatus.tsx's fmtProjTemp. Treat as missing so the chart
// doesn't plot a flat -100 °C ribbon for projector-less devices.
function projTempAccessor(field: keyof TelemetryPoint) {
  return (p: TelemetryPoint): number | null => {
    const v = p[field] as number | null;
    if (v === null || v === undefined) return null;
    if (v === -100) return null;
    return v;
  };
}

// Sensor NTC thermistor reports -99 as the "no measurement" sentinel
// (open circuit / sensor not connected) — see DE-135.
function sensorTempAccessor(p: TelemetryPoint): number | null {
  const v = p.sensor_temp_int_avg;
  if (v === null || v === undefined) return null;
  if (v === -99) return null;
  return v;
}

// proj_lamp_hrs_max / proj_op_hrs_max are Postgres BIGINT columns; the
// backend serializer (node-pg default) renders them as JSON strings,
// not numbers. The TelemetryPoint type declares them as `number | null`,
// but the wire format is `string | null`. Coerce defensively so
// downstream Number.isFinite / arithmetic / scaling work uniformly.
function bigintAccessor(v: number | null): number | null {
  if (v === null || v === undefined) return null;
  // `typeof v` may be 'string' at runtime even though TS says 'number'.
  const n = typeof v === 'number' ? v : Number(v as unknown as string);
  return Number.isFinite(n) ? n : null;
}

export const TELEMETRY_SERIES: TelemetrySeries[] = [
  // ── Host ────────────────────────────────────────────────────────
  {
    id: 'cpu',
    label: 'CPU',
    section: 'Host',
    unit: '%',
    accessor: (p) => p.cpu_percent_avg,
    format: (v) => `${v.toFixed(1)} %`,
    color: '#00B4D0', // accent (cyan)
    yMin: 0,
    yMax: 100,
  },
  {
    id: 'cpu_temp',
    label: 'CPU temp',
    section: 'Host',
    unit: '°C',
    accessor: (p) => p.cpu_temp_c_max,
    format: (v) => `${v.toFixed(1)} °C`,
    color: '#a8541f', // warn
  },
  {
    id: 'memory',
    label: 'Memory',
    section: 'Host',
    unit: 'MB',
    accessor: (p) => p.mem_used_mb_avg,
    // Switch to GB above 1024 MB so multi-GB devices read naturally.
    format: (v) => (v >= 1024 ? `${(v / 1024).toFixed(2)} GB` : `${Math.round(v)} MB`),
    color: '#3b82f6', // blue-500
  },

  // ── Network ─────────────────────────────────────────────────────
  {
    id: 'modem',
    label: 'Modem signal',
    section: 'Network',
    unit: '',
    accessor: (p) => p.modem_signal_max,
    format: (v) => `${Math.round(v)}`,
    color: '#0d9488', // teal-700 — same teal as the timeline's boot ticks
    yMin: 0,
    yMax: 100,
  },

  // ── Sensors ─────────────────────────────────────────────────────
  {
    id: 'box_temp',
    label: 'Box temp',
    section: 'Sensors',
    unit: '°C',
    accessor: sensorTempAccessor,
    format: (v) => `${v.toFixed(1)} °C`,
    color: '#ea580c', // orange-600 — heat without overlapping warn (which is on CPU temp)
  },
  {
    id: 'humidity',
    label: 'Humidity',
    section: 'Sensors',
    unit: '%',
    accessor: (p) => p.sensor_humidity_avg,
    format: (v) => `${v.toFixed(1)} %`,
    color: '#06b6d4', // cyan — water-coded
    yMin: 0,
    yMax: 100,
  },
  {
    id: 'remote_battery',
    label: 'Remote battery',
    section: 'Sensors',
    unit: '%',
    accessor: batteryAccessor,
    format: (v) => `${Math.round(v)} %`,
    color: '#84cc16', // lime
    yMin: 0,
    yMax: 100,
  },

  // ── Projector ───────────────────────────────────────────────────
  {
    id: 'proj_intake',
    label: 'Intake',
    section: 'Projector',
    unit: '°C',
    accessor: projTempAccessor('proj_temp_intake_avg'),
    format: (v) => `${Math.round(v)} °C`,
    color: '#22c55e', // green-500
  },
  {
    id: 'proj_exhaust',
    label: 'Exhaust',
    section: 'Projector',
    unit: '°C',
    accessor: projTempAccessor('proj_temp_exhaust_avg'),
    format: (v) => `${Math.round(v)} °C`,
    color: '#a8541f', // warn — exhaust is the hottest by nature
  },
  {
    id: 'proj_light1',
    label: 'Light 1',
    section: 'Projector',
    unit: '°C',
    accessor: projTempAccessor('proj_temp_light1_max'),
    format: (v) => `${Math.round(v)} °C`,
    color: '#6366f1', // indigo-500
  },
  {
    id: 'proj_light2',
    label: 'Light 2',
    section: 'Projector',
    unit: '°C',
    accessor: projTempAccessor('proj_temp_light2_max'),
    format: (v) => `${Math.round(v)} °C`,
    color: '#a855f7', // purple-500
  },
  {
    id: 'lamp_hours',
    label: 'Lamp hours',
    section: 'Projector',
    unit: 'h',
    // Backend returns this as a JSON string (Postgres BIGINT → text in
    // node-pg's default serializer). Coerce so Number.isFinite() and
    // arithmetic work; preserve null for absent readings.
    accessor: (p) => bigintAccessor(p.proj_lamp_hrs_max),
    format: (v) => `${Math.round(v)} h`,
    color: '#94a3b8', // slate-400 — cumulative counter, low visual priority
  },
  {
    id: 'op_hours',
    label: 'Op hours',
    section: 'Projector',
    unit: 'h',
    accessor: (p) => bigintAccessor(p.proj_op_hrs_max),
    format: (v) => `${Math.round(v)} h`,
    color: '#cbd5e1', // slate-300 — same role as lamp hours, slightly lighter
  },
];

export const TELEMETRY_SECTIONS: TelemetrySection[] = [
  'Host',
  'Network',
  'Sensors',
  'Projector',
];

/** Stable id → series lookup for tooltip resolution and URL parsing. */
export const SERIES_BY_ID: Record<string, TelemetrySeries> = Object.fromEntries(
  TELEMETRY_SERIES.map((s) => [s.id, s]),
);

/**
 * Parse the URL `hide` param (comma-separated series ids). Unknown ids
 * are silently dropped so deleting a series in the registry doesn't
 * leave stale URLs in a broken state.
 */
export function parseHide(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set();
  const out = new Set<string>();
  for (const id of raw.split(',')) {
    const trimmed = id.trim();
    if (trimmed && SERIES_BY_ID[trimmed]) out.add(trimmed);
  }
  return out;
}

/** Inverse of parseHide — produce the URL-shaped string. */
export function serializeHide(hidden: Set<string>): string {
  // Stable order so toggling A then B produces the same URL as B then A —
  // makes shared links idempotent and easier to diff.
  return TELEMETRY_SERIES.filter((s) => hidden.has(s.id))
    .map((s) => s.id)
    .join(',');
}
